/**
 * @copyright Copyright 2024-2024 Epic Systems Corporation
 * @file hook for getting a generic questionnaire component
 * @module Epic.AppOrchard.Core.Questionnaire.Hooks
 */

import { useAsync } from "@epic/react-async-hook";
import {
	IQuestionnaireQuestionOptionSave,
	IQuestionnaireQuestionSave,
	ISaveQuestionnaireRequestModel,
} from "ao/data";
import {
	IGenericResponseModel,
	IQuestionnaireCategoryForEditing,
	IQuestionnaireCategoryForEditingWithDisplayValues,
	IQuestionnaireOptionForEditing,
	IQuestionnaireQuestionForEditing,
	QuestionnaireQuestionType,
} from "ao/types";
import { useCallback, useEffect, useState } from "react";
import { saveQuestionnaire } from "./data/data";
import {
	checkIfAllQuestionsAnswered,
	convertToCategoriesWithComputedDisplayValues,
	copyCategories,
	setShouldShow,
} from "./data/Helpers";

/**
 * React hook for getting a state and save handlers for a questionnaire
 * @param externalCategories
 * @param questionnaireVersion
 * @param newRecord Indicates that the record associated with this questionnaire (app or org) is new or not.
 * @param appId
 * @param onChangeCallback
 * @param onSaveSuccessCallback
 * @param onSaveFailureCallback
 * @returns
 */
const useQuestionnaire = (
	externalCategories: IQuestionnaireCategoryForEditing[],
	questionnaireVersion: number,
	newRecord: boolean,
	appId?: number,
	allowEmptyQuestions?: boolean,
	onChangeCallback?: (responses: IQuestionnaireCategoryForEditing[]) => void,
	onSaveSuccessCallback?: (response: IGenericResponseModel) => void,
	onSaveFailureCallback?: (response: IGenericResponseModel) => void,
) => {
	const [internalCategories, setInternalCategories] = useState<
		IQuestionnaireCategoryForEditingWithDisplayValues[]
	>([]);
	const [displayConditionDependencyMap, setDisplayConditionDependencyMap] = useState<Map<number, number[]>>(
		new Map(),
	);
	const [allQuestionsAnswered, setAllQuestionsAnswered] = useState<boolean>(false);
	const [isQuestionnaireSaving, setIsQuestionnaireSaving] = useState<boolean>(false);

	const resetQuestionnaire = useCallback(() => {
		const [newCategories, dependencyMap] = convertToCategoriesWithComputedDisplayValues(
			externalCategories,
		);
		setDisplayConditionDependencyMap(dependencyMap);
		setShouldShow(newCategories, dependencyMap);
		setAllQuestionsAnswered(checkIfAllQuestionsAnswered(newCategories));
		setInternalCategories(newCategories);
	}, [externalCategories]);

	useEffect(() => {
		resetQuestionnaire();
	}, [resetQuestionnaire]);

	const answerChangeHandler = useCallback(
		(
			categoryId: number,
			question: IQuestionnaireQuestionForEditing,
			option?: IQuestionnaireOptionForEditing,
			optionIsSelected?: boolean,
			freetext?: string,
		) => {
			const newCategories = copyCategories(internalCategories);
			const newQuestionCategory = newCategories.find(
				(category: IQuestionnaireCategoryForEditing) => category.id === categoryId,
			);

			if (!newQuestionCategory) {
				return;
			}

			const newQuestion = newQuestionCategory.questions.find(
				(copiedQuestion: IQuestionnaireQuestionForEditing) => copiedQuestion.id === question.id,
			);

			if (!newQuestion) {
				return;
			}

			let optionSelectionChanged = false;

			if (newQuestion.type === QuestionnaireQuestionType.Freetext) {
				newQuestion.responseText = freetext ?? "";
			} else if (newQuestion.type === QuestionnaireQuestionType.Radio && option) {
				for (const newOption of newQuestion.options) {
					if (newOption.id === option.id) {
						if (!newOption.selected) {
							optionSelectionChanged = true;
						}
						newOption.selected = true;
						if (newOption.supportsFreetext) {
							newOption.freetextResponse = freetext ?? "";
						}
					} else {
						newOption.selected = false;
					}
				}
			} else if (newQuestion.type === QuestionnaireQuestionType.Checkboxes && option) {
				const newOptionIsExclusive = option.override;
				for (const newOption of newQuestion.options) {
					if (newOption.id === option.id) {
						if (newOption.selected !== (optionIsSelected ?? false)) {
							optionSelectionChanged = true;
						}
						newOption.selected = optionIsSelected ?? false;
						if (newOption.supportsFreetext) {
							newOption.freetextResponse = freetext ?? "";
						}
					} else if (newOptionIsExclusive && optionIsSelected) {
						newOption.selected = false;
					}
				}
			}

			if (optionSelectionChanged) {
				setShouldShow(newCategories, displayConditionDependencyMap, question.id);
			}

			setAllQuestionsAnswered(checkIfAllQuestionsAnswered(newCategories));
			setInternalCategories(newCategories);

			if (onChangeCallback) {
				onChangeCallback(newCategories);
			}
		},

		[displayConditionDependencyMap, internalCategories, onChangeCallback],
	);

	const [savingState, save] = useAsync(saveQuestionnaire, {
		executeImmediately: false,
		displayName: "saveQuestionnaire",
		onFailure: (response: IGenericResponseModel) => {
			if (onSaveFailureCallback) {
				onSaveFailureCallback(response);
			}
			setIsQuestionnaireSaving(false);
		},
		onSuccess: (response: IGenericResponseModel) => {
			if (onSaveSuccessCallback) {
				onSaveSuccessCallback(response);
			}
			setIsQuestionnaireSaving(false);
		},
	});

	const buildSaveModel = useCallback(() => {
		const responses: IQuestionnaireQuestionSave[] = [];
		internalCategories.forEach((category) =>
			category.questions.forEach((question) => {
				if (!question.shouldShow) {
					return;
				}
				if (question.type === QuestionnaireQuestionType.Freetext) {
					responses.push({
						id: question.id,
						responseText: question.responseText,
						selectedOptions: [],
					});
				} else if (question.type === QuestionnaireQuestionType.Radio) {
					const selectedOption = question.options.find((option) => option.selected);
					if (!selectedOption) {
						return;
					}
					responses.push({
						id: question.id,
						responseText: "",
						selectedOptions: [
							{ id: selectedOption.id, freetextResponse: selectedOption.freetextResponse },
						],
					});
				} else if (question.type === QuestionnaireQuestionType.Checkboxes) {
					const selectedOptions = question.options.filter((option) => option.selected);
					if (!selectedOptions || selectedOptions.length === 0) {
						return;
					}
					const selectedCheckboxOptions: IQuestionnaireQuestionOptionSave[] = [];
					selectedOptions.forEach((selectedOption) => {
						selectedCheckboxOptions.push({
							id: selectedOption.id,
							freetextResponse: selectedOption.freetextResponse,
						});
					});
					if (selectedCheckboxOptions.length > 0) {
						responses.push({
							id: question.id,
							responseText: "",
							selectedOptions: selectedCheckboxOptions,
						});
					}
				}
			}),
		);

		const results: ISaveQuestionnaireRequestModel = {
			appId,
			questionnaireVersion,
			newRecord,
			questions: responses,
		};

		return results;
	}, [appId, internalCategories, newRecord, questionnaireVersion]);

	const saveHandler = useCallback(() => {
		if (isQuestionnaireSaving) {
			return;
		}

		if (!allowEmptyQuestions && !allQuestionsAnswered) {
			return;
		}

		const results = buildSaveModel();

		setIsQuestionnaireSaving(true);
		save(results);
	}, [allQuestionsAnswered, allowEmptyQuestions, buildSaveModel, isQuestionnaireSaving, save]);

	return {
		categories: internalCategories,
		saveHandler,
		buildSaveModel,
		answerChangeHandler,
		savingState,
		allQuestionsAnswered,
		resetQuestionnaire,
	};
};

export default useQuestionnaire;
